import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import "./productFIlter.css";
import { exportAllUserProductsUrl } from "../../api/endpoints";
import { Download, Search } from "lucide-react";
import ProgressBar from "./progressBar/ProgressBar";
import { uiAlert } from "../../components/alerts/customAlert/uiAlert";

const STATUS_OPTIONS = ["all", "low", "sale", "damaged", "subunit"];

export default function ProductCatalogueFilter({
  openInsights,
  totalProducts,
  lowStockCount,
  onSaleCount,
  search,
  setSearch,
  storeFilter,
  setStoreFilter,
  statusFilter,
  setStatusFilter,
  stores = [],
  userSocket,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const { user } = useSelector((state) => state.webStoreUser);

  useEffect(() => {
    const handleProgress = (data) => {
      setExportProgress(data.percentage);

      if (data.completed) {
        setExporting(false);
      }

      if (data.error) {
        setExporting(false);
      }
    };

    userSocket.on("productExportProgress", handleProgress);

    return () => {
      userSocket.off("productExportProgress", handleProgress);
    };
  }, []);

  const [draft, setDraft] = useState({
    store: storeFilter || "all",
    status: statusFilter || "All statuses",
  });

  const storeOptions = [{ label: "All stores", value: "all" }, ...stores.map((s) => ({ label: s.storeName, value: s._id }))];

  const hasActiveFilters = (storeFilter && storeFilter !== "all") || (statusFilter && statusFilter !== "All statuses");

  const openModal = () => {
    setDraft({
      store: storeFilter || "all",
      status: statusFilter || "All statuses",
    });
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const applyFilters = () => {
    setStoreFilter(draft.store);
    setStatusFilter(draft.status);
    closeModal();
  };

  const resetFilters = () => {
    setDraft({ store: "all", status: "All statuses" });
  };

  const exportProducts = async () => {
    if (!user) {
      uiAlert("You must be logged in to export products.", "error");
      return;
    }

    if (user.userType?.userType?.includes("basic") || user.userType?.userType?.includes("starter")) {
      return uiAlert("Your current plan does not allow exporting products. Please upgrade to a premium plan to access this feature.", "error");
    }

    setExportProgress(0);
    setExporting(true);

    try {
      const token = localStorage.getItem("accessToken");

      const response = await fetch(exportAllUserProductsUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to export products");
      }

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "products.xlsx";

      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      uiAlert("Failed to export products. Please try again.", "error");
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      {exporting && <ProgressBar exportProgress={exportProgress} />}
      <div className='pcf__wrapper'>
        <div className='pcf__card'>
          <div className='pcf__card-header'>
            <div>
              <p className='pcf__card-title'>Product Catalogue</p>
              <p className='pcf__card-meta'>
                {totalProducts} products · {lowStockCount} low stock · {onSaleCount} on sale
              </p>
            </div>
            <button onClick={openInsights} className='pcf__insights-btn'>
              <svg
                width='15'
                height='15'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                aria-hidden='true'
              >
                <rect x='3' y='12' width='4' height='9' />
                <rect x='10' y='8' width='4' height='13' />
                <rect x='17' y='4' width='4' height='17' />
              </svg>
              Insights
            </button>
          </div>

          <div className='pcf__search-row'>
            <div className='stock-search-container'>
              <Search className='stock-search-icon' size={16} />
              <input
                type='text'
                className='stock-search-input'
                placeholder='Name, brand, barcode...'
                value={search || ""}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div
              style={{
                display: "flex",
                gap: "6px",
              }}
            >
              <button onClick={exportProducts} className='sl-sales-record-export-btn'>
                <Download size={15} />
                <span>Export</span>
              </button>

              <button className={`pcf__filter-btn${hasActiveFilters ? " pcf__filter-btn--active" : ""}`} onClick={openModal} aria-label='Open filters'>
                <svg
                  width='18'
                  height='18'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  aria-hidden='true'
                >
                  <line x1='4' y1='6' x2='20' y2='6' />
                  <line x1='8' y1='12' x2='16' y2='12' />
                  <line x1='11' y1='18' x2='13' y2='18' />
                </svg>
                {hasActiveFilters && <span className='pcf__filter-badge' aria-hidden='true' />}
              </button>
            </div>
          </div>
        </div>

        {modalOpen && (
          <div className='pcf__overlay' onClick={closeModal} role='dialog' aria-modal='true' aria-label='Product filters'>
            <div className='pcf__modal' onClick={(e) => e.stopPropagation()}>
              <div className='pcf__modal-header'>
                <p className='pcf__modal-title'>Filters</p>
                <button className='pcf__close-btn' onClick={closeModal} aria-label='Close filters'>
                  <svg
                    width='16'
                    height='16'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    aria-hidden='true'
                  >
                    <line x1='18' y1='6' x2='6' y2='18' />
                    <line x1='6' y1='6' x2='18' y2='18' />
                  </svg>
                </button>
              </div>

              <div className='pcf__modal-body'>
                {/* Store filter */}
                <div className='pcf__filter-group'>
                  <p className='pcf__group-label'>Store</p>
                  <div className='pcf__pills'>
                    {storeOptions.map((opt) => (
                      <button
                        key={opt.value}
                        className={`pcf__pill${draft.store === opt.value ? " pcf__pill--active" : ""}`}
                        onClick={() => setDraft((prev) => ({ ...prev, store: opt.value }))}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Status filter */}
                <div className='pcf__filter-group'>
                  <p className='pcf__group-label'>Status</p>
                  <div className='pcf__pills'>
                    {STATUS_OPTIONS.map((option) => (
                      <button
                        key={option}
                        className={`pcf__pill${draft.status === option ? " pcf__pill--active" : ""}`}
                        onClick={() => setDraft((prev) => ({ ...prev, status: option }))}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className='pcf__modal-footer'>
                <button className='pcf__reset-btn' onClick={resetFilters}>
                  Reset
                </button>
                <button className='pcf__apply-btn' onClick={applyFilters}>
                  Apply filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
